import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const projectTypes = [
  'Channel Letter Sign', 'Monument Sign', 'LED / Illuminated Sign',
  'Storefront Sign', 'Vehicle Wrap / Fleet Graphics', 'Interior Sign / ADA',
  'Banners / Event Signage', 'DTF / UV Transfers', 'Other / Not Sure',
];

type Status = 'idle' | 'submitting' | 'success' | 'error';

const trust = [
  { icon: 'ri-shield-check-fill', text: 'Licensed & insured in Austin TX' },
  { icon: 'ri-star-fill', text: '5-star rated by Austin businesses' },
  { icon: 'ri-time-fill', text: 'Response within 1 business day' },
  { icon: 'ri-map-pin-2-fill', text: 'Serving all of Central Texas' },
  { icon: 'ri-tools-fill', text: 'Full-service: design → install' },
];

export default function FinalCTA() {
  const [status, setStatus] = useState<Status>('idle');
  const [chars, setChars] = useState(0);
  const { ref, visible } = useScrollAnimation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const msg = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (msg.length > 500) return;
    setStatus('submitting');
    const data = new URLSearchParams();
    Array.from(form.elements).forEach((el) => {
      const input = el as HTMLInputElement;
      if (input.name && input.value) data.append(input.name, input.value);
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/d760a04bmgf2o8mm6v1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) { form.reset(); setChars(0); }
    } catch { setStatus('error'); }
  };

  return (
    <section id="quote" className="relative overflow-hidden bg-[#080808]">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/c9c5ac4f6062d1e84436c0b111d35448.jpg"
          alt="Capital Area Signs premium installation"
          className="w-full h-full object-cover object-center opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#080808] via-[#080808]/90 to-[#080808]/80" />
      </div>

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-8 py-28">
        {/* Top headline */}
        <div className={`text-center mb-16 reveal ${visible ? 'visible' : ''}`}>
          <p className="eyebrow mb-5">Final Step</p>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}>
            Make Your Business<br />
            <em className="not-italic text-gradient-gold">Impossible to Miss</em>
          </h2>
          <span className="gold-bar mx-auto block mb-6" />
          <p className="text-white/45 text-base max-w-xl mx-auto leading-relaxed">
            Get a professionally engineered sign that commands attention, reinforces your brand, and drives real foot traffic.
          </p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start reveal ${visible ? 'visible' : ''}`}
          style={{ transitionDelay: '0.15s' }}>

          {/* Left: trust + contact */}
          <div>
            <div className="mb-10">
              <p className="text-white/30 text-xs uppercase tracking-widest font-bold mb-6">Why Request a Quote</p>
              <div className="space-y-4">
                {trust.map((t, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <span className="w-9 h-9 flex items-center justify-center flex-shrink-0 border border-[#C4962A]/30">
                      <i className={`${t.icon} text-[#C4962A] text-sm`} />
                    </span>
                    <p className="text-white/60 text-sm">{t.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact block */}
            <div className="bg-[#141414] border border-white/5 p-8 mb-6">
              <p className="eyebrow mb-6">Or Contact Directly</p>
              <div className="space-y-5">
                <a href="tel:+15127990115" className="flex items-center gap-4 group cursor-pointer">
                  <span className="w-10 h-10 flex items-center justify-center bg-[#C4962A]/10 border border-[#C4962A]/30 flex-shrink-0">
                    <i className="ri-phone-fill text-[#C4962A] text-sm" />
                  </span>
                  <div>
                    <p className="text-white/30 text-xs uppercase tracking-widest mb-0.5">Phone</p>
                    <p className="text-white font-bold group-hover:text-[#C4962A] transition-colors">(512) 799-0115</p>
                  </div>
                </a>
                <a href="mailto:chris@capitalareasign.com" className="flex items-center gap-4 group cursor-pointer">
                  <span className="w-10 h-10 flex items-center justify-center bg-[#C4962A]/10 border border-[#C4962A]/30 flex-shrink-0">
                    <i className="ri-mail-line text-[#C4962A] text-sm" />
                  </span>
                  <div>
                    <p className="text-white/30 text-xs uppercase tracking-widest mb-0.5">Email</p>
                    <p className="text-white/80 text-sm group-hover:text-[#C4962A] transition-colors">chris@capitalareasign.com</p>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="bg-[#111111] border border-white/8 p-8 md:p-10">
            <p className="eyebrow mb-3">Free Custom Quote</p>
            <h3 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Tell Us About Your Project
            </h3>

            {status === 'success' ? (
              <div className="py-14 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-[#C4962A]/10 border border-[#C4962A]/30 mx-auto mb-5">
                  <i className="ri-checkbox-circle-fill text-[#C4962A] text-2xl" />
                </div>
                <h4 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Quote Request Received
                </h4>
                <p className="text-white/45 text-sm mb-6">We&apos;ll follow up within one business day. For immediate help, call <a href="tel:+15127990115" className="text-[#C4962A] font-semibold">(512) 799-0115</a>.</p>
                <button onClick={() => setStatus('idle')} className="btn-outline-gold cursor-pointer">
                  Submit Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/30 text-xs uppercase tracking-widest mb-2">Full Name *</label>
                    <input type="text" name="name" required placeholder="John Smith"
                      className="w-full bg-[#1A1A1A] border border-white/10 text-white placeholder-white/20 text-sm px-4 py-3 focus:outline-none focus:border-[#C4962A]/60 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-white/30 text-xs uppercase tracking-widest mb-2">Phone *</label>
                    <input type="tel" name="phone" required placeholder="(512) 000-0000"
                      className="w-full bg-[#1A1A1A] border border-white/10 text-white placeholder-white/20 text-sm px-4 py-3 focus:outline-none focus:border-[#C4962A]/60 transition-colors" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/30 text-xs uppercase tracking-widest mb-2">Email *</label>
                    <input type="email" name="email" required placeholder="you@business.com"
                      className="w-full bg-[#1A1A1A] border border-white/10 text-white placeholder-white/20 text-sm px-4 py-3 focus:outline-none focus:border-[#C4962A]/60 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-white/30 text-xs uppercase tracking-widest mb-2">Business Name *</label>
                    <input type="text" name="business_name" required placeholder="Your Business"
                      className="w-full bg-[#1A1A1A] border border-white/10 text-white placeholder-white/20 text-sm px-4 py-3 focus:outline-none focus:border-[#C4962A]/60 transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="block text-white/30 text-xs uppercase tracking-widest mb-2">Project Type</label>
                  <select name="project_type"
                    className="w-full bg-[#1A1A1A] border border-white/10 text-white/60 text-sm px-4 py-3 focus:outline-none focus:border-[#C4962A]/60 transition-colors">
                    <option value="">Select project type...</option>
                    {projectTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-white/30 text-xs uppercase tracking-widest mb-2">
                    Project Details <span className="normal-case font-normal text-white/20">(max 500 chars)</span>
                  </label>
                  <textarea name="message" rows={4} maxLength={500}
                    placeholder="Describe your signage project — size, location, timeline, requirements..."
                    onChange={(e) => setChars(e.target.value.length)}
                    className="w-full bg-[#1A1A1A] border border-white/10 text-white placeholder-white/20 text-sm px-4 py-3 focus:outline-none focus:border-[#C4962A]/60 transition-colors resize-none" />
                  <p className={`text-xs text-right mt-1 ${chars > 450 ? 'text-amber-500' : 'text-white/20'}`}>{chars}/500</p>
                </div>
                {status === 'error' && (
                  <p className="text-red-400 text-xs text-center">Something went wrong. Please call (512) 799-0115.</p>
                )}
                <button type="submit" disabled={status === 'submitting' || chars > 500}
                  className="btn-gold w-full justify-center disabled:opacity-50 cursor-pointer">
                  {status === 'submitting' ? (
                    <><i className="ri-loader-4-line animate-spin" /> Sending...</>
                  ) : (
                    <>Request My Custom Quote <i className="ri-arrow-right-line" /></>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
